import { useCallback, useEffect, useState } from 'react';
import type { Sessao } from '../../auth';
import { dataHoraCurta, podeFazer } from '../../acesso';
import type { Eu, Falha } from '../../acesso';
import { SemAcesso } from '../../design/Estados';
import { Icone } from '../../design/Icone';
import { useVeCusto } from '../../design/sigilo';
import { reais } from '../../produtos';
import { data, lerAlertas, lerCatalogo, origem, rotuloDoEstado } from '../../validades';
import type { Catalogo, LinhaDoCatalogo } from '../../validades';
import { ErroDaLista, Esqueleto } from '../equipe/Equipe';
import type { LancadosNoPeriodo } from '../../lancados-no-periodo';
import type { Periodo } from '../../paineis';
import { Alertas } from './Alertas';
import { DrawerDeLotes } from './DrawerDeLotes';
import { BuscaParaNovoLote } from './NovoLote';
import { Vencimentos } from './Vencimentos';
import { VencidosPorMes } from './VencidosPorMes';
import '../equipe/acesso.css';
import '../equipe/aprovacoes.css';
import '../produtos/produtos.css';
import '../conta/conta.css';
import './prevencidos.css';

/**
 * TELAS 25 e 26 — PRÉ-VENCIDOS: catálogo e lotes, vencimentos e alertas (Bloco 5).
 *
 * "Validade é do lote, não do produto." O catálogo mostra o lote MAIS URGENTE de cada produto e
 * abre a lista completa. Nada é inventado: sem validade é "não informada", sem saldo por lote é
 * "indisponível", e vencido tem estado próprio — não aparece como oportunidade de venda.
 */

export type AbaDoPreVencido = 'catalogo' | 'vencimentos' | 'vencidos' | 'alertas';
type Aba = AbaDoPreVencido;
type Estado =
  | { readonly fase: 'carregando' }
  | { readonly fase: 'erro'; readonly falha: Falha }
  | { readonly fase: 'pronto'; readonly dados: Catalogo };

const CHIPS: readonly (readonly [string, string, string?])[] = [
  ['30', 'Em 30 dias'],
  ['7', '7 dias'],
  ['60', '60 dias'],
  ['90', '90 dias'],
  ['vencidos', 'Vencidos', 'alerta'],
  ['sem_validade', 'Sem validade'],
  ['todos', 'Todos'],
];

export function PreVencidos({
  sessao,
  eu,
  abaFixa,
  lancados,
  validade,
}: {
  sessao: Sessao;
  eu: Eu | null;
  /** Dentro do módulo Pré-vencidos a aba vem da barra do módulo (uma barra só). */
  abaFixa?: AbaDoPreVencido | undefined;
  /** O filtro de período do módulo: quanto de cada produto foi lançado nele (catálogo). */
  lancados?: LancadosNoPeriodo | undefined;
  /** O filtro de período pela validade (vencimentos, vencidos) ou pela decisão (alertas). */
  validade?: Periodo | null | undefined;
}) {
  const [abaLocal, setAba] = useState<Aba>('catalogo');
  const aba = abaFixa ?? abaLocal;
  const [alertasAbertos, setAlertasAbertos] = useState<number | null>(null);
  const configura = podeFazer(eu, 'produtos.configurar');

  const contarAlertas = useCallback(async () => {
    const r = await lerAlertas();
    if (r.ok) setAlertasAbertos(r.dados.ativos.filter((a) => a.estado === 'aberto').length);
  }, []);
  useEffect(() => {
    void contarAlertas();
  }, [contarAlertas]);

  return (
    <div className="ac pv">
      {!abaFixa && (
        <div className="ap-abas" role="tablist">
          {(
            [
              ['catalogo', 'Catálogo'],
              ['vencimentos', 'Vencimentos'],
              ['vencidos', 'Vencidos por mês'],
              ['alertas', 'Alertas'],
            ] as const
          ).map(([id, rotulo]) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={aba === id}
              className={`ap-aba${aba === id ? ' ap-aba-ativa' : ''}`}
              onClick={() => setAba(id)}
            >
              {rotulo}
              {id === 'alertas' && alertasAbertos !== null && alertasAbertos > 0 && (
                <span className="pv-aba-contador">{alertasAbertos}</span>
              )}
            </button>
          ))}
          <span className="ac-espaco" />
          <span className="ac-nota ap-abas-nota">janela oficial e regras vêm da configuração</span>
        </div>
      )}
      {aba === 'catalogo' && (
        <CatalogoDePreVencidos sessao={sessao} configura={configura} lancados={lancados} />
      )}
      {aba === 'vencimentos' && (
        <Vencimentos sessao={sessao} configura={configura} periodo={validade ?? null} />
      )}
      {aba === 'vencidos' && <VencidosPorMes periodo={validade ?? null} />}
      {aba === 'alertas' && (
        <Alertas
          sessao={sessao}
          configura={configura}
          aoMudar={() => void contarAlertas()}
          periodo={validade ?? null}
        />
      )}
    </div>
  );
}

function CatalogoDePreVencidos({
  sessao,
  configura,
  lancados,
}: {
  sessao: Sessao;
  configura: boolean;
  lancados?: LancadosNoPeriodo | undefined;
}) {
  const veCusto = useVeCusto();
  const [filtro, setFiltro] = useState('30');
  const [busca, setBusca] = useState('');
  const [buscaAplicada, setBuscaAplicada] = useState('');
  const [fabricante, setFabricante] = useState('');
  const [pagina, setPagina] = useState(1);
  const [estado, setEstado] = useState<Estado>({ fase: 'carregando' });
  const [aberto, setAberto] = useState<{ id: string; lancar: boolean } | null>(null);
  const [novoLote, setNovoLote] = useState(false);

  useEffect(() => {
    const t = window.setTimeout(() => {
      setBuscaAplicada(busca);
      setPagina(1);
    }, 300);
    return () => window.clearTimeout(t);
  }, [busca]);

  const carregar = useCallback(async () => {
    setEstado((e) => (e.fase === 'pronto' ? e : { fase: 'carregando' }));
    const r = await lerCatalogo({
      filtro,
      busca: buscaAplicada,
      fabricante,
      pagina,
      lancados: lancados ?? null,
    });
    setEstado(r.ok ? { fase: 'pronto', dados: r.dados } : { fase: 'erro', falha: r.falha });
  }, [filtro, buscaAplicada, fabricante, pagina, lancados?.de, lancados?.ate, lancados?.so]);
  // Trocou o período: volta para a primeira página.
  useEffect(() => setPagina(1), [lancados?.de, lancados?.ate, lancados?.so]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  if (estado.fase === 'erro' && estado.falha.tipo === 'sem-acesso')
    return <SemAcesso area="Pré-vencidos" />;
  const dados = estado.fase === 'pronto' ? estado.dados : null;
  const r = dados?.resumo;

  return (
    <div className="ac ac-cheia pv-catalogo">
      <div className="ac-cabeca">
        <div>
          <h1 className="ac-titulo">Catálogo de pré-vencidos</h1>
          <div className="ac-subtitulo">
            {r
              ? `${r.produtos.toLocaleString('pt-BR')} produtos · ${r.lotes.toLocaleString('pt-BR')} lotes acompanhados · lote e produto contados separadamente`
              : 'Carregando…'}
          </div>
        </div>
        <div className="ac-acoes">
          {configura && (
            <a
              className="ac-botao"
              href="/api/lotes/exportar.csv?programa=pre_vencido"
              download
              title="Planilha dos lotes com saldo, em ordem de validade (abre no Excel)"
            >
              <Icone nome="down" tamanho={14} traco={1.8} />
              Exportar estoque
            </a>
          )}
          {configura && (
            <button
              type="button"
              className="ac-botao ac-botao-primario"
              onClick={() => setNovoLote(true)}
            >
              <Icone nome="plus" tamanho={14} traco={1.8} />
              Novo lote
            </button>
          )}
          <label className="ac-busca pv-busca">
            <Icone nome="search" tamanho={14} traco={1.6} />
            <input
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Produto, EAN, código, lote ou fabricante"
              aria-label="Buscar pré-vencidos"
            />
          </label>
        </div>
      </div>

      <div className="ac-kpis">
        <Kpi rotulo="VENCIDOS" faixa={r?.vencidos} tom="alerta" />
        <Kpi rotulo="EM 7 DIAS" faixa={r?.em7} tom="aviso" />
        <Kpi rotulo="EM 30 DIAS" faixa={r?.em30} />
        <Kpi rotulo="EM 90 DIAS" faixa={r?.em90} />
        <div className="ac-kpi">
          <div className="ac-kpi-rotulo">SEM VALIDADE</div>
          <div className="ac-kpi-valor pd-kpi-neutro">{r ? r.semValidade : '—'}</div>
          <div className="pd-kpi-sub">não informada</div>
        </div>
      </div>

      <div className="ac-filtros">
        {CHIPS.map(([id, rotulo, tom]) => (
          <button
            key={id}
            type="button"
            aria-pressed={filtro === id}
            className={`ac-chip${tom === 'alerta' ? ' ac-chip-alerta' : ''}${filtro === id ? ' ac-chip-ativo' : ''}`}
            onClick={() => {
              setFiltro(id);
              setPagina(1);
            }}
          >
            {rotulo}
          </button>
        ))}
        <span className="ac-divisor" />
        <label className="ac-seletor">
          <span className="ac-seletor-rotulo">Fabricante</span>
          <select
            value={fabricante}
            onChange={(e) => {
              setFabricante(e.target.value);
              setPagina(1);
            }}
            aria-label="Fabricante"
          >
            <option value="">Todos</option>
            {(dados?.fabricantes ?? []).map((f) => (
              <option key={f}>{f}</option>
            ))}
          </select>
          <Icone nome="chev-d" tamanho={12} traco={1.6} />
        </label>
        <span className="ac-espaco" />
        {veCusto && (
          <span className="pv-valor">
            <Icone nome="money" tamanho={13} traco={1.6} />
            Valor a custo na janela:{' '}
            <b>
              {r ? (r.valorACustoNaJanela ? reais(r.valorACustoNaJanela) : 'indisponível') : '—'}
            </b>
            <span className="ac-fraco"> · custo do servidor</span>
          </span>
        )}
      </div>

      <div className="ac-cartao ac-cartao-tabela">
        {estado.fase === 'carregando' && <Esqueleto linhas={6} />}
        {estado.fase === 'erro' && (
          <div style={{ padding: 16 }}>
            <ErroDaLista
              titulo="Não foi possível carregar os lotes"
              texto="O catálogo de produtos continua disponível."
              aoTentar={() => void carregar()}
            />
          </div>
        )}
        {dados && dados.itens.length === 0 && (
          <div className="ac-vazio">
            <div className="ac-vazio-titulo">
              {r && r.lotes === 0
                ? 'Nenhum lote de pré-vencido cadastrado'
                : 'Nenhum lote dentro deste filtro'}
            </div>
            <div className="ac-vazio-texto">
              {r && r.lotes === 0
                ? 'Os lotes entram pelo lançamento (abra um produto em Produtos) ou pela migração do 1.0.'
                : 'Nada vence neste intervalo com os filtros atuais.'}
            </div>
          </div>
        )}
        {dados && dados.itens.length > 0 && (
          <div className="ac-tabela-rolo">
            <table className="ac-tabela">
              <thead>
                <tr>
                  <th>PRODUTO</th>
                  <th>LOTE MAIS URGENTE</th>
                  <th>VALIDADE</th>
                  <th className="ac-direita">DIAS</th>
                  <th className="ac-direita">QTD NO LOTE</th>
                  <th className="ac-direita ac-col-larga">LOTES</th>
                  <th className="ac-direita ac-col-larga">ESTOQUE</th>
                  {lancados && <th className="ac-direita">LANÇADOS NO PERÍODO</th>}
                  <th className="ac-col-larga">FONTE · ATUALIZADO</th>
                  <th>ESTADO</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {dados.itens.map((l) => (
                  <Linha
                    key={l.productId}
                    l={l}
                    comLancados={!!lancados}
                    aoAbrir={() => setAberto({ id: l.productId, lancar: false })}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="ac-rodape-tabela">
          <span>
            {dados && dados.total > 0
              ? `${String((dados.pagina - 1) * dados.tamanhoDaPagina + 1)}–${String((dados.pagina - 1) * dados.tamanhoDaPagina + dados.itens.length)} de ${dados.total.toLocaleString('pt-BR')} produtos`
              : ' '}
            {dados && dados.total > dados.tamanhoDaPagina && (
              <span className="pd-paginas">
                <button
                  type="button"
                  className="ac-link"
                  disabled={dados.pagina <= 1}
                  onClick={() => setPagina((n) => n - 1)}
                >
                  Anterior
                </button>
                <button
                  type="button"
                  className="ac-link"
                  disabled={dados.pagina * dados.tamanhoDaPagina >= dados.total}
                  onClick={() => setPagina((n) => n + 1)}
                >
                  Próxima
                </button>
              </span>
            )}
          </span>
          <span>
            Estado com texto e ícone, nunca só cor · vencido tem tratamento próprio e não entra como
            oportunidade de venda
          </span>
        </div>
      </div>

      <div className="ac-cards">
        {dados?.itens.map((l) => (
          <CardDoLote
            key={l.productId}
            l={l}
            aoAbrir={() => setAberto({ id: l.productId, lancar: false })}
          />
        ))}
        {dados && dados.itens.length === 0 && (
          <div className="ac-cartao">
            <div className="ac-vazio">
              <div className="ac-vazio-titulo">Nenhum lote dentro da janela</div>
              <div className="ac-vazio-texto">
                Nada vence neste intervalo com os filtros atuais.
              </div>
            </div>
          </div>
        )}
      </div>

      {novoLote && (
        <BuscaParaNovoLote
          aoFechar={() => setNovoLote(false)}
          aoEscolher={(id) => {
            setNovoLote(false);
            setAberto({ id, lancar: true });
          }}
        />
      )}
      {aberto && (
        <DrawerDeLotes
          key={aberto.id}
          productId={aberto.id}
          sessao={sessao}
          configura={configura}
          iniciarLancando={aberto.lancar}
          aoFechar={() => setAberto(null)}
          aoMudar={() => void carregar()}
        />
      )}
    </div>
  );
}

export function Selo({ texto, tom, icone }: { texto: string; tom: string; icone?: boolean }) {
  return (
    <span className={`pv-selo pv-selo-${tom}`}>
      {icone && <Icone nome="warn" tamanho={11} traco={1.9} />}
      {texto}
    </span>
  );
}

function Linha({
  l,
  aoAbrir,
  comLancados,
}: {
  l: LinhaDoCatalogo;
  aoAbrir: () => void;
  comLancados: boolean;
}) {
  const lote = l.loteMaisUrgente;
  const est = rotuloDoEstado(lote.estado, lote.quantidade === null);
  const dias = 'dias' in lote.estado ? lote.estado.dias : null;
  const vencido = lote.estado.estado === 'vencido' || lote.estado.estado === 'vencido_no_mes';
  return (
    <tr className={vencido ? 'pv-linha-vencida' : undefined}>
      <td>
        <div className="ac-pessoa-nome">{l.descricao}</div>
        <div className="pd-codigos">{[l.codigo, l.fabricante].filter(Boolean).join(' · ')}</div>
      </td>
      <td className="ac-mono">{lote.codigo ?? '—'}</td>
      <td className={lote.validade ? 'ac-num' : 'ac-cinza'}>{data(lote.validade)}</td>
      <td className={`ac-direita pv-dias pv-dias-${est.tom}`}>
        {dias === null ? <span className="pd-traco">—</span> : String(dias).replace('-', '−')}
      </td>
      <td className="ac-direita ac-num">
        {lote.quantidade === null ? (
          <span className="pd-aviso-txt">indisponível</span>
        ) : (
          lote.quantidade
        )}
      </td>
      <td className="ac-direita ac-num ac-col-larga">{l.lotesAtivos}</td>
      <td className="ac-direita ac-num ac-col-larga">{l.estoqueTotal ?? '—'}</td>
      {comLancados && (
        <td className="ac-direita ac-num">
          {l.lancadosNoPeriodo ? <strong>{l.lancadosNoPeriodo}</strong> : '0'}
        </td>
      )}
      <td className="ac-cinza ac-col-larga">
        {origem(lote.origem)} · {dataHoraCurta(lote.atualizadoEm)}
      </td>
      <td>
        <Selo texto={est.texto} tom={est.tom} icone={vencido} />
      </td>
      <td className="ac-direita">
        <button type="button" className="ac-link" onClick={aoAbrir}>
          Ver lotes
        </button>
      </td>
    </tr>
  );
}

function CardDoLote({ l, aoAbrir }: { l: LinhaDoCatalogo; aoAbrir: () => void }) {
  const lote = l.loteMaisUrgente;
  const est = rotuloDoEstado(lote.estado, lote.quantidade === null);
  const vencido = lote.estado.estado === 'vencido' || lote.estado.estado === 'vencido_no_mes';
  return (
    <button
      type="button"
      className={`ac-card${vencido ? ' ac-card-alerta' : ''}`}
      onClick={aoAbrir}
    >
      <div className="ac-card-linha">
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="ac-card-nome">{l.descricao}</div>
          <div className="ac-card-sub">
            lote {lote.codigo ?? '—'}
            {l.codigo ? ` · ${l.codigo}` : ''}
            {l.lancadosNoPeriodo !== undefined && ` · ${String(l.lancadosNoPeriodo)} lançados`}
          </div>
        </div>
        <Selo texto={est.texto} tom={est.tom} />
      </div>
      {vencido ? (
        <div className="pd-card-texto">
          Venceu em {data(lote.validade)} · {lote.quantidade ?? '?'} unidades. Não é oportunidade de
          venda — siga o procedimento da loja para produto vencido.
        </div>
      ) : (
        <div className="pd-card-faixas">
          <div>
            <div className="pd-card-faixa-rotulo">VALIDADE</div>
            <div className="pd-card-faixa-valor">
              {lote.validade ? data(lote.validade).slice(0, 5) : '—'}
            </div>
          </div>
          <div>
            <div className="pd-card-faixa-rotulo">NO LOTE</div>
            <div className="pd-card-faixa-valor">{lote.quantidade ?? 'indisponível'}</div>
          </div>
        </div>
      )}
    </button>
  );
}

function Kpi({
  rotulo,
  faixa,
  tom,
}: {
  rotulo: string;
  faixa: { lotes: number; unidades: string } | undefined;
  tom?: 'alerta' | 'aviso';
}) {
  const ativo = faixa !== undefined && faixa.lotes > 0;
  return (
    <div
      className={`ac-kpi${ativo && tom === 'alerta' ? ' ac-kpi-alerta' : ''}${ativo && tom === 'aviso' ? ' ac-kpi-aviso' : ''}`}
    >
      <div className="ac-kpi-rotulo">{rotulo}</div>
      <div className="ac-kpi-valor">{faixa ? faixa.lotes.toLocaleString('pt-BR') : '—'}</div>
      <div className="pd-kpi-sub">{faixa ? `lotes · ${faixa.unidades} un` : ' '}</div>
    </div>
  );
}
